import React, { useEffect, useRef, useState } from "react";
import { DBTag } from "models/TagModel";
import Form from "react-bootstrap/Form";
import {
  SubmitHandler,
  useForm,
  useFieldArray,
  useWatch,
} from "react-hook-form";
import { ReportDetailsType } from "models/ReportDetailsType";
import { transformReportModelToDetailsType } from "components/Reports/reportAssets/transformReportModelToDetailsModel";
import { isEmptyObject } from "common/helpers/isEmptyObject";
import { AddCroppedImageArrayType } from "models/CropImageObject";
import { useUpdateReportDetailsByControllerMutation } from "features/api/reports/reportsSlice";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  convertBase64ToFile,
  logFormData,
} from "common/helpers/convertBase64oFile";
import { useGetTagListQuery } from "features/api/contentManagement/contentManagementApiSlice";
import { useNavigate } from "react-router-dom";
import { LocalStorageKey, useGetUserData } from "common/helpers/userData";
import { useLocationForReportType } from "hooks/useLocationForReportType";
import { FormStepsProps } from "./FormStepsProps";
import DetailsFromSection from "./FormComponents/DetailsFromSection";
import VideoAndCroppingFormSection from "./FormComponents/VideoAndCroppingFormSection";
import TagsFormSection from "./FormComponents/TagsFormSection";
import DecisionFormSection from "./FormComponents/DecisionFormSection";
import ButtonsSEctionReportDetails from "../buttonsSetionReportDetails";
import ErrorsContainer from "./FormComponents/ErrorsContainer/ErrorsContainer";
import { initialCroppedImages } from "./formStepsUtils";

const VideoAndDetailsStep = ({
  currStep,
  setCurrStep,
  setModalShow,
  reportDetails,
}: FormStepsProps) => {
  const { t } = useTranslation();
  const selectedRole = useGetUserData(LocalStorageKey.SelectedRole)?.value;

  const reportDetailsForVideoAndDetailsStep =
    transformReportModelToDetailsType(reportDetails);

  const defaultValues = {
    ...reportDetailsForVideoAndDetailsStep,
  };
  const locationString = useLocationForReportType();
  const isMessagesScreen = locationString === "followup-messages";
  const navigate = useNavigate();
  const [isBackNavigation, setIsBackNavigation] = useState(false);
  const isReadonly = locationString === "followup" || isMessagesScreen;

  const {
    handleSubmit,
    control,
    setError,
    formState: { errors, isSubmitting },
    setValue,
    getValues,
    trigger,
  } = useForm<ReportDetailsType>({
    defaultValues,
  });
  const [updateReportDetailsByController] =
    useUpdateReportDetailsByControllerMutation();

  // <state for cropping images from video>
  const [canvasImages, setCanvasImages] = useState({
    licenseNumberPicture: useRef(),
    offencePicture: useRef(),
    extraPicture: useRef(),
    roadProblemPicture: useRef(),
  });
  const [currentCroppedButton, setCurrentCroppedButton] = useState(null);
  const [addCroppedImageArray, setAddCroppedImageArray] = useState<
    AddCroppedImageArrayType[]
  >(
    initialCroppedImages({
      roadProblemPicture: defaultValues?.roadProblemPicture || null,
      extraPicture: defaultValues?.extraPicture || null,
      offencePicture: defaultValues?.offencePicture || null,
      licenseNumberPicture: defaultValues?.licenseNumberPicture || null,
    })
  );
  // <state for cropping images from video/>

  const formValues = useWatch({
    control,
    defaultValue: {
      ...reportDetailsForVideoAndDetailsStep,
    },
  });

  useEffect(() => {
    !isReadonly && trigger(); // Trigger form validation for all fields
  }, [isReadonly, formValues, trigger, errors, setError]);

  useEffect(() => {
    if (isBackNavigation) {
      navigate(-1);
    }
  }, [isBackNavigation, navigate]);

  const { data: tags } = useGetTagListQuery();

  const selectedTags = getValues("tags");

  const tagsToRender = tags
    ?.filter((tag: DBTag) => !tag.disable)
    ?.map((tag: DBTag) => ({
      tagCode: tag.id,
      tagName: tag.name,
      active: selectedTags?.includes(tag.id),
    }))
    .sort((a, b) => b.active - a.active);

  const skip = () => {
    setCurrStep(currStep + 1);
  };

  const vehicleDetailsFields = [
    "modelType",
    "carColor",
    "carModel",
    "manufacturerName",
  ];

  // set the vehicle details to send to server, if the field is equal to "אין מידע" set null
  const arangeDataOfVehicleDetails = (
    obj: ReportDetailsType,
    formDataToUpdate: FormData
  ) => {
    for (const property of vehicleDetailsFields) {
      if (obj[property] === "אין מידע") {
        formDataToUpdate.append(property, null);
      } else formDataToUpdate.append(property, obj[property]);
    }
  };

  const onSubmit: SubmitHandler<ReportDetailsType> = (data) => {
    if (isEmptyObject(errors)) {
      const {
        licenseNumberPicture,
        offencePicture,
        extraPicture,
        roadProblemPicture,
        ...restData
      } = data;

      const arangeDataToServer = new FormData();

      const pictureFields = [
        {
          base64: licenseNumberPicture,
          fieldName: "licenseNumberPicture",
          fileName: "license_number_picture",
          extension: "jpeg",
          type: "image/jpeg",
        },
        {
          base64: offencePicture,
          fieldName: "offencePicture",
          fileName: "offence_picture",
          extension: "jpeg",
          type: "image/jpeg",
        },
        {
          base64: extraPicture,
          fieldName: "extraPicture",
          fileName: "extra_picture",
          extension: "jpeg",
          type: "image/jpeg",
        },
        {
          base64: roadProblemPicture,
          fieldName: "roadProblemPicture",
          fileName: "road_problem_picture",
          extension: "jpeg",
          type: "image/jpeg",
        },
      ];

      pictureFields.forEach((field) => {
        if (field.base64 !== null) {
          const file = convertBase64ToFile(
            field.base64,
            field.fileName,
            field.extension,
            field.type
          );
          arangeDataToServer.append(field.fieldName, file);
        }
      });

      arangeDataOfVehicleDetails(restData, arangeDataToServer);

      Object.keys(restData)
        .filter((key) => !vehicleDetailsFields.includes(key))
        .forEach((key) => {
          if (Object.prototype.hasOwnProperty.call(restData, key)) {
            if (key === "tags") {
              const tagsArray = restData[key];
              tagsArray.forEach((tag, index) => {
                arangeDataToServer.append(`${key}[${index}]`, String(tag));
              });
            } else {
              arangeDataToServer.append(
                key,
                restData[key] === null ? "" : restData[key]
              );
            }
          }
        });

      // log form data for developer
      logFormData(arangeDataToServer);

      updateReportDetailsByController({
        role: selectedRole,
        body: arangeDataToServer,
      }).then((resp) => {
        if (resp?.error?.status === 401) {
          toast.error(t("loginError"));
          return;
        }
        if (resp?.error?.status === 500) {
          toast.error(t("serverLoginError"));
          return;
        }
        if (resp?.error?.status === 400) {
          toast.error(t("validationError"));
          return;
        }
        if (resp?.data === false) {
          toast.error(t("requestFailedEndPointError"));
          return;
        }
        if (resp.data) setCurrStep(currStep + 1);
      });
    }

    // how to use this form:
    // "get submited data:" {data}
    // "get submited errors:" {errors}
    // "get specific field value: watch input value by passing the name of it" {watch("licenseNumber")}
    //
  };
  return (
    <Form
      className="reports-details-form-container"
      onSubmit={handleSubmit(onSubmit)}
    >
      <VideoAndCroppingFormSection
        currStep={currStep}
        control={control}
        errors={errors}
        canvasImages={canvasImages}
        setCanvasImages={setCanvasImages}
        addCroppedImageArray={addCroppedImageArray}
        setAddCroppedImageArray={setAddCroppedImageArray}
        currentCroppedButton={currentCroppedButton}
        setCurrentCroppedButton={setCurrentCroppedButton}
        setValue={setValue}
        setError={setError}
        reportOffenceStartTime={reportDetails?.reportOffenceStartTime}
      />
      <DetailsFromSection
        control={control}
        errors={errors}
        setValue={setValue}
        canvasImages={canvasImages}
        setCanvasImages={setCanvasImages}
        addCroppedImageArray={addCroppedImageArray}
        setAddCroppedImageArray={setAddCroppedImageArray}
        currentCroppedButton={currentCroppedButton}
        setCurrentCroppedButton={setCurrentCroppedButton}
        getValues={getValues}
        isAlreadyDuplicated={
          reportDetails.reportIsDuplicate ||
          (reportDetails.reportIsOrigin &&
            reportDetails.duplicatedReportsIds.length > 0)
        }
        reportIsDuplicate={reportDetails.reportIsDuplicate}
      />
      <TagsFormSection
        control={control}
        tagsToRender={tagsToRender}
        setValue={setValue}
        getValues={getValues}
      />
      <DecisionFormSection
        getValues={getValues}
        control={control}
        errors={errors}
        useFieldArray={useFieldArray}
        setValue={setValue}
        reportIsDuplicate={reportDetails.reportIsDuplicate}
      />
      {!isEmptyObject(errors) && <ErrorsContainer errors={errors} />}
      {!isMessagesScreen && (
        <ButtonsSEctionReportDetails
          setModalShow={isReadonly ? setIsBackNavigation : setModalShow}
          currStep={currStep}
          isSubmitButtonDisable={isSubmitting}
          handleSubmit={isReadonly ? () => skip() : () => {}}
        />
      )}
    </Form>
  );
};
export default VideoAndDetailsStep;
